import { cn } from '@/utils/utils';
import { MessagesSquareIcon } from 'lucide-react';

import { useChatsSidebar } from '@/hooks/useChatsSidebar';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';
import { type WorkspaceItem, useWorkspace } from '@/hooks/useWorkspace';
import Link from 'next/link';
import { useMemo, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { VList } from 'virtua';
import { Button } from '../Button/Button';
import { Icon } from '../ui/Icon';
import SidebarChatDropdown from './SidebarChatDropdown';

/**
 * An overlay sidebar that sits to the right of the main left sidebar and shows
 * the user's most recent chats.
 */
const SidebarChatsOverlay = () => {
  const { showChatsSidebar, setShowChatsSidebar } = useChatsSidebar();
  const { setShowLeftSidebar } = useLeftSidebar();

  const isMobile = useIsMobile();

  const { data } = useWorkspace();

  const ref = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(ref as React.RefObject<HTMLElement>, (v) => {
    if (!showChatsSidebar) return;

    const target = v.target as HTMLElement;
    // Check if the clicked element or any of its parents has the sidebar-chats-button ID
    const isChatsButton = target?.closest('#sidebar-chats-button') !== null;
    const isChatDropdown = target?.closest('#sidebar-chat-dropdown') !== null;

    if (!isChatsButton && !isChatDropdown) {
      setShowChatsSidebar(false);
    }
  });

  const allChats = useMemo(() => {
    if (!data) return [];
    const chats = [] as WorkspaceItem[];

    for (const e of data.entities ?? []) {
      if (e.type === 'CHAT') chats.push(e);
      if (e.chats) chats.push(...e.chats.filter((c) => c.type === 'CHAT'));
    }

    return chats.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-0 left-0 h-full w-[240px] bg-background border-r max-sm:z-[99]  max-sm:fixed z-40 transition-transform duration-200 ease-in-out',
        showChatsSidebar ? 'translate-x-0 shadow-feint' : '-translate-x-full',
      )}
    >
      <div className="flex flex-col h-full p-2">
        <p className="text-sm px-2 py-2 font-semibold text-muted-foreground">
          Previous chats
        </p>
        <VList
          id="sidebar-chats-list"
          className="scrollbar-hide h-full flex flex-col gap-0.5"
          overscan={8}
        >
          {allChats.length > 0 ? (
            allChats.map((chat) => (
              <div key={chat.id} className="relative group">
                <Link
                  onClick={() => {
                    if (isMobile) setShowLeftSidebar(false);
                    setShowChatsSidebar(false);
                  }}
                  href={`/b/${chat.parentChatbotId ?? chat.id}?chatId=${chat.id}&referrer=sidebar`}
                >
                  <Button size="xs" variant="sidebar">
                    <Icon className="flex-shrink-0" as={MessagesSquareIcon} />
                    <span className="truncate pr-4">{chat.name}</span>
                  </Button>
                </Link>
                <SidebarChatDropdown chat={chat} />
              </div>
            ))
          ) : (
            <p className="text-textGray4 pt-1 pl-2 text-sm">
              No chats available
            </p>
          )}
        </VList>
      </div>
    </div>
  );
};

export default SidebarChatsOverlay;
